import { useEffect, useState, useCallback } from "react";
import ConversationDrawer from "./ConversationDrawer";
import { listProjects, type ProjectSummary } from "../api/projects";

export type Project = ProjectSummary;

type ProjectKind = "addon" | "tool" | "workspace";

/**
 * The registry API only returns {id, name}; infer a display kind + glyph from
 * the name so the Forge sidebar can render the coloured kind glyph. Purely
 * cosmetic — no behaviour depends on it.
 */
function inferKind(p: Project): ProjectKind {
  const hay = `${p.id} ${p.name}`.toLowerCase();
  if (hay.includes("workspace") || hay.includes(" ai") || hay.endsWith("ai")) return "workspace";
  if (hay.includes("add-on") || hay.includes("addon")) return "addon";
  if (hay.includes("tool") || hay.includes("bot") || hay.includes("cli")) return "tool";
  return "addon";
}

const KIND_LABEL: Record<ProjectKind, string> = {
  addon: "Add-On",
  tool: "Tool",
  workspace: "Workspace",
};

function glyphChar(name: string): string {
  const letters = name.replace(/[^A-Za-z]/g, "");
  return (letters.slice(0, 1) || name.slice(0, 1) || "?").toUpperCase();
}

type Props = {
  /**
   * Optional explicit project list. When omitted, Sidebar fetches the
   * registry via listProjects() and manages loading/empty/error states
   * internally (REG-03).
   */
  projects?: Project[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Fired after a successful registry fetch (internal-fetch mode only). */
  onProjectsLoaded?: (projects: Project[]) => void;
  conversationRefreshKey?: number;
  /** Called when a conversation is opened (plain click = replace, Shift-click = additive). */
  onOpenConversation?: (
    ref: { project: string; conversationId: number; branch: string | null; worktreeDir?: string | null },
    additive: boolean,
  ) => void;
  /** Called when a new conversation is created. */
  onNewConversation?: (conversationId: number, branch: string | null, worktreeDir?: string | null) => void;
};

export default function Sidebar({
  projects: projectsProp,
  selectedId,
  onSelect,
  onProjectsLoaded,
  conversationRefreshKey,
  onOpenConversation,
  onNewConversation,
}: Props) {
  const internalMode = projectsProp === undefined;
  const [fetched, setFetched] = useState<Project[] | null>(null);
  const [loading, setLoading] = useState<boolean>(internalMode);
  const [error, setError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (!internalMode) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listProjects()
      .then((list) => {
        if (cancelled) return;
        setFetched(list);
        setLoading(false);
        onProjectsLoaded?.(list);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message || "failed to load projects");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [internalMode, retryTick, onProjectsLoaded]);

  // Retry fetch when TokenSetup saves a token (it writes localStorage; same-tab writes
  // do not fire 'storage', so listen for our own custom event AND foreign-tab storage).
  useEffect(() => {
    if (!internalMode) return;
    const bump = () => setRetryTick((n) => n + 1);
    window.addEventListener("storage", bump);
    window.addEventListener("thunderay-ai:token-changed", bump);
    return () => {
      window.removeEventListener("storage", bump);
      window.removeEventListener("thunderay-ai:token-changed", bump);
    };
  }, [internalMode]);

  const retry = useCallback(() => setRetryTick((n) => n + 1), []);

  const projects = projectsProp ?? fetched ?? [];

  const handleSelectConversation = (
    conversationId: number,
    branch: string | null,
    additive: boolean,
    worktreeDir?: string | null,
  ) => {
    if (!selectedId) return;
    onOpenConversation?.({ project: selectedId, conversationId, branch, worktreeDir }, additive);
  };

  const handleNewConversation = (conversationId: number, branch: string | null, worktreeDir?: string | null) => {
    if (!selectedId) return;
    if (onOpenConversation) {
      onOpenConversation({ project: selectedId, conversationId, branch, worktreeDir }, false);
    }
    onNewConversation?.(conversationId, branch, worktreeDir);
  };

  if (internalMode && loading) {
    return <div className="sidebar-state sidebar-loading">Loading projects…</div>;
  }
  if (internalMode && error) {
    return (
      <div className="sidebar-state sidebar-error">
        <div>Failed to load projects: {error}</div>
        <button type="button" className="sidebar-retry" onClick={retry}>
          Retry
        </button>
      </div>
    );
  }
  if (internalMode && projects.length === 0) {
    return <div className="sidebar-state sidebar-empty">No projects configured.</div>;
  }

  return (
    <ul className="sidebar-list">
      {projects.map((p) => {
        const selected = p.id === selectedId;
        const kind = inferKind(p);
        return (
          <li key={p.id}>
            <button
              type="button"
              className={`project-name${selected ? " selected" : ""}`}
              aria-current={selected ? "page" : undefined}
              onClick={() => onSelect(p.id)}
            >
              <span className={`glyph glyph-${kind}`} aria-hidden="true">
                {glyphChar(p.name)}
              </span>
              <span className="project-body">
                <span className="project-label">{p.name}</span>
                <span className="project-sub">{KIND_LABEL[kind]}</span>
              </span>
            </button>
            {selected && (
              <ConversationDrawer
                projectId={p.id}
                projectName={p.name}
                expanded={true}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
                refreshKey={conversationRefreshKey}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
